import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from 'components/Button';
import { grantFaqToReofferBuyer } from 'api/faq-sponsor';
import { newsroomError, sendStoryOffer } from 'api/newsroom';
import {
    handleHidePopup,
    handleShowPopup,
    setPopupConfig,
    setPopupLoading,
} from 'store/ux';
import {
    alertStillOpen,
    faqSponsorStatusLabel,
    offerSendChoice,
    publicationOffer,
    publicationOfferBlocker,
    publicationOfferText,
    reofferGrantBlocker,
} from 'utils/faq-sponsor';
import { offerPricePence, priceLabel } from 'utils/research-sponsor';
import FaqReview from './FaqReview';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';

const CHOICES = [
    {
        value: 'faq',
        label: 'FAQ sponsorship: £50+VAT for one month',
    },
    {
        value: 'full',
        label: 'Full sponsorship: top of the story, a banner and the FAQ slot',
    },
    { value: 'none', label: 'None' },
];

// Evergreen Sponsor: the story's one offer to the alert's responders, chosen
// once the story is live. Nothing goes out until Send is pressed.
const FaqOfferFields = ({
    alert,
    // A re-offer buyer bought this slot with the story sponsorship.
    reofferSold,
    researchRow,
    story,
    draft,
    dirty,
    onChange,
    onStoryChange,
    onResearchRowChange,
}) => {
    const dispatch = useDispatch();
    const sponsored = Boolean(alert.isSponsored);
    const status = faqSponsorStatusLabel(story);
    // Answers are written to these questions, matched by position.
    const locked = Boolean(story?.faqSponsorStatus);
    // Kept here: approving or declining changes what the review panel shows.
    const [reviewNotice, setReviewNotice] = useState(null);
    const [granting, setGranting] = useState(false);
    const [sending, setSending] = useState(false);
    const [choice, setChoice] = useState('none');
    // Pre-filled from the alert's own sponsorship price, if it had one.
    const [price, setPrice] = useState(
        researchRow?.pricePence ? String(researchRow.pricePence / 100) : ''
    );
    useEffect(() => {
        if (researchRow?.pricePence) {
            setPrice((typed) => typed || String(researchRow.pricePence / 100));
        }
    }, [researchRow?.pricePence]);
    const grantBlocker = reofferGrantBlocker(story, draft);
    const alertOpen = alertStillOpen(alert);
    const offer = publicationOffer(story, researchRow);
    const retry = offer?.choice === 'full' && !offer.notified;
    const pricePence = retry ? offer.pricePence : offerPricePence(price);
    const sendChoice = offerSendChoice(offer, choice);
    const blocker = publicationOfferBlocker({
        choice: sendChoice,
        alert,
        story,
        row: researchRow,
        dirty,
        pricePence,
    });

    const grant = async () => {
        setGranting(true);
        setReviewNotice(null);
        try {
            onStoryChange(await grantFaqToReofferBuyer(alert.id));
            setReviewNotice({
                type: 'ok',
                text: 'The slot is theirs. They have been emailed to send their answers.',
            });
        } catch (err) {
            setReviewNotice({
                type: 'error',
                text: newsroomError(err, 'Could not give them the slot.'),
            });
        } finally {
            setGranting(false);
        }
    };

    const send = async () => {
        setSending(true);
        setReviewNotice(null);
        try {
            const res = await sendStoryOffer(
                alert.id,
                sendChoice === 'full'
                    ? { choice: 'full', pricePence }
                    : { choice: 'faq' }
            );
            onStoryChange(res.story);
            onResearchRowChange?.(res.research);
            setReviewNotice(
                res.sent
                    ? { type: 'ok', text: 'Offer sent to the responders.' }
                    : {
                          type: 'error',
                          text: 'The offer email could not be sent. Press Send again in a few minutes.',
                      }
            );
        } catch (err) {
            setReviewNotice({
                type: 'error',
                text: newsroomError(err, 'Could not send the offer.'),
            });
        } finally {
            setSending(false);
        }
    };

    // It goes to every responder at once and can't be taken back.
    const confirmSend = () => {
        const what =
            sendChoice === 'full'
                ? `the full sponsorship at ${priceLabel(
                      pricePence
                  )}+VAT, open for seven days`
                : 'the FAQ sponsorship at £50+VAT for one month';
        dispatch(handleShowPopup());
        dispatch(
            setPopupConfig({
                title: 'Send the offer?',
                body: `Every expert who responded is emailed ${what}. A story gets one offer, so this can't be changed afterwards.`,
                confirmTitle: 'Send offer',
                cancelTitle: 'Cancel',
                onCancel: () => dispatch(handleHidePopup()),
                onConfirm: async () => {
                    dispatch(setPopupLoading(true));
                    await send();
                    dispatch(setPopupLoading(false));
                    dispatch(handleHidePopup());
                },
            })
        );
    };

    const setQuestion = (index, value) =>
        onChange(
            'faqQuestions',
            draft.faqQuestions.map((q, i) => (i === index ? value : q))
        );

    const showQuestions =
        !sponsored &&
        (choice !== 'none' ||
            offer ||
            reofferSold ||
            Boolean(story?.faqHeading));

    return (
        <fieldset className={styles.faqOffer}>
            <legend className={styles.kicker}>Evergreen Sponsor</legend>

            {sponsored ? (
                <p className={styles.hint}>
                    Alert is sponsored; the sponsor&rsquo;s answers run here.
                </p>
            ) : reofferSold ? (
                <p className={styles.hint}>
                    Sold with the full sponsorship: check the heading and
                    questions with the buyer, save the story, then give them the
                    slot. They get the same email as a paying sponsor, and the
                    slot runs as long as the sponsorship.
                </p>
            ) : offer ? (
                <p className={styles.hint}>{publicationOfferText(offer)}</p>
            ) : (
                <div role="radiogroup" aria-labelledby="publication-offer">
                    <p className={styles.hint} id="publication-offer">
                        One offer per story, emailed once to every expert who
                        responded, after you press Send. First to pay gets it.
                    </p>
                    {CHOICES.map(({ value, label }) => {
                        const closedOnly = value === 'full' && alertOpen;
                        return (
                            <div key={value}>
                                <label className={styles.checkboxLabel}>
                                    <input
                                        type="radio"
                                        name="publication-offer"
                                        value={value}
                                        checked={choice === value}
                                        disabled={closedOnly}
                                        aria-describedby={
                                            closedOnly
                                                ? 'full-offer-closed-only'
                                                : undefined
                                        }
                                        onChange={() => setChoice(value)}
                                    />
                                    {label}
                                </label>
                                {closedOnly && (
                                    <p
                                        className={styles.hint}
                                        id="full-offer-closed-only"
                                    >
                                        Available once the alert has closed.
                                    </p>
                                )}
                            </div>
                        );
                    })}
                    {choice === 'full' && (
                        <>
                            <label
                                className={styles.label}
                                htmlFor="offer-price"
                            >
                                Price in pounds, + VAT
                            </label>
                            <input
                                id="offer-price"
                                className={styles.input}
                                inputMode="numeric"
                                aria-describedby="offer-price-hint"
                                value={price}
                                placeholder="e.g. 500"
                                onChange={(e) => setPrice(e.target.value)}
                            />
                            <p className={styles.hint} id="offer-price-hint">
                                {researchRow?.pricePence
                                    ? `The alert's sponsorship was ${priceLabel(
                                          researchRow.pricePence
                                      )}+VAT. Open to the respondents for seven days.`
                                    : 'The alert had no sponsorship price: type one. Open to the respondents for seven days.'}
                            </p>
                        </>
                    )}
                </div>
            )}
            {!sponsored && !reofferSold && sendChoice !== 'none' && (
                <>
                    <div className={styles.editorActions}>
                        <Button
                            title={
                                retry ? 'Send the email again' : 'Send offer'
                            }
                            theme={'success'}
                            loading={sending}
                            disabled={Boolean(blocker)}
                            onClick={confirmSend}
                        />
                    </div>
                    {blocker && <p className={styles.hint}>{blocker}</p>}
                </>
            )}
            {status && <p className={styles.faqStatus}>{status}</p>}
            {reofferSold && !story?.faqSponsorStatus && (
                <>
                    <div className={styles.editorActions}>
                        <Button
                            title={'Give the FAQ slot to the re-offer buyer'}
                            theme={'success'}
                            loading={granting}
                            disabled={Boolean(grantBlocker)}
                            onClick={grant}
                        />
                    </div>
                    {grantBlocker && (
                        <p className={styles.hint}>{grantBlocker}</p>
                    )}
                </>
            )}
            {story?.faqSponsorStatus && (
                <FaqReview
                    alertId={alert.id}
                    story={story}
                    onStoryChange={onStoryChange}
                    onNotice={setReviewNotice}
                />
            )}
            {reviewNotice && (
                <p
                    className={
                        reviewNotice.type === 'error' ? styles.error : styles.ok
                    }
                    role={reviewNotice.type === 'error' ? 'alert' : 'status'}
                >
                    {reviewNotice.text}
                </p>
            )}
            {locked && (
                <p className={styles.hint}>
                    The heading and questions are fixed now that the slot is
                    sold.
                </p>
            )}

            {showQuestions && (
                <>
                    <label className={styles.label} htmlFor="faq-heading">
                        FAQ heading
                    </label>
                    <input
                        id="faq-heading"
                        className={styles.input}
                        value={draft.faqHeading}
                        maxLength={255}
                        disabled={locked}
                        placeholder="e.g. First-time buyer mortgages: your questions answered"
                        onChange={(e) => onChange('faqHeading', e.target.value)}
                    />
                    <span className={styles.label}>
                        Questions (at least one)
                    </span>
                    {draft.faqQuestions.map((question, i) => (
                        <input
                            key={i}
                            aria-label={`Question ${i + 1}`}
                            className={[styles.input, styles.faqQuestion].join(
                                ' '
                            )}
                            value={question}
                            maxLength={255}
                            disabled={locked}
                            placeholder={`Question ${i + 1}`}
                            onChange={(e) => setQuestion(i, e.target.value)}
                        />
                    ))}
                </>
            )}
        </fieldset>
    );
};

export default FaqOfferFields;
